import { useRef, useCallback, useLayoutEffect, type ReactElement } from "react";
import { Virtualizer, VirtualizerHandle } from "virtua";
import { cn } from "../lib/utils";

export interface ReversePaginatedListProps {
  /**
   * Array of elements to render. Each child must have a unique `key` prop
   * for proper prepend detection and scroll position maintenance.
   *
   * @example
   * ```tsx
   * {messages.map(msg => <MessageItem key={msg.id} {...msg} />)}
   * ```
   */
  children: ReactElement[];
  /**
   * Callback triggered when scrolling to the top (for loading older messages)
   */
  onTopReached?: () => void;
  /**
   * Callback triggered when scrolling to the bottom (for detecting newest messages)
   */
  onBottomReached?: () => void;
  className?: string;
  /**
   * Threshold in pixels from the edge to trigger callbacks.
   * Defaults to 100px (typical for chat interfaces).
   */
  threshold?: number;
  /**
   * Whether to automatically scroll to bottom on initial mount.
   * Defaults to true.
   */
  autoScrollToBottom?: boolean;
  onFocusWithin?: (context: { isAtBottom: boolean; isAtTop: boolean }) => void;
  onClickWithin?: (context: { isAtBottom: boolean; isAtTop: boolean }) => void;
}

/**
 * A virtualized scrollable container component for reverse pagination (chat-like interfaces).
 * Automatically scrolls to the bottom on mount and maintains scroll position when
 * older messages are prepended at the top.
 *
 * Uses virtua's Virtualizer with the `shift` prop for optimal chat/messaging UX.
 *
 * @example
 * ```tsx
 * <ReversePaginatedList
 *   onTopReached={() => loadOlderMessages()}
 *   className="h-96"
 * >
 *   {messages.map(msg => <Message key={msg.id} {...msg} />)}
 * </ReversePaginatedList>
 * ```
 */
export function ReversePaginatedList({
  children,
  onTopReached,
  onBottomReached,
  className,
  threshold = 100,
  autoScrollToBottom = true,
  onFocusWithin,
  onClickWithin,
}: ReversePaginatedListProps) {
  const virtualizerRef = useRef<VirtualizerHandle>(null);
  const isPrepending = useRef(false);
  const shouldStickToBottom = useRef(true);
  const isInitialMount = useRef(true);
  const topReachedRef = useRef(false);
  const bottomReachedRef = useRef(false);
  const prevChildrenCountRef = useRef(0);
  const prevFirstKeyRef = useRef<string | null>(null);
  const prevLastKeyRef = useRef<string | null>(null);

  // Detect when items are prepended by comparing children count and first item key
  const itemCount = children.length;
  const childrenIncreased = itemCount > prevChildrenCountRef.current;
  const firstKey = children[0]?.key?.toString() ?? null;
  const lastKey = children[children.length - 1]?.key?.toString() ?? null;

  // Set shift to true ONLY when items are prepended at the top (first key changed)
  // Don't use shift when items are appended at the bottom (last key changed)
  if (childrenIncreased && !isInitialMount.current) {
    const isPreprending = firstKey !== prevFirstKeyRef.current;
    const isAppending = lastKey !== prevLastKeyRef.current;

    // Only set shift if items were prepended (first key changed) but not appended
    isPrepending.current = isPreprending && !isAppending;
  }

  // Auto-scroll to bottom on initial mount
  useLayoutEffect(() => {
    if (isInitialMount.current && autoScrollToBottom) {
      if (itemCount > 0) {
        // Delay scroll to ensure virtualizer is ready
        requestAnimationFrame(() => {
          virtualizerRef.current?.scrollToIndex(itemCount - 1, {
            align: "end",
          });
        });
        // Since we're auto-scrolling to bottom, the user starts at the bottom
        if (onBottomReached) {
          bottomReachedRef.current = true;
          onBottomReached();
        }
      }
      isInitialMount.current = false;
    }
  }, [children, autoScrollToBottom, itemCount, onBottomReached]);

  // Auto-scroll to bottom when new items are appended and user is at bottom
  // This must run BEFORE the refs are updated so we can detect changes
  useLayoutEffect(() => {
    // Skip initial mount or when items are removed
    if (isInitialMount.current || itemCount <= prevChildrenCountRef.current) return;

    // Check if items were appended (last key changed)
    const wasAppending = lastKey !== prevLastKeyRef.current;

    // If user was at bottom and items were appended, scroll to new bottom
    if (wasAppending && shouldStickToBottom.current && itemCount > 0) {
      requestAnimationFrame(() => {
        virtualizerRef.current?.scrollToIndex(itemCount - 1, {
          align: "end",
        });
      });
    }
  }, [children, itemCount, lastKey]);

  // Reset prepending flag and update previous count/keys after render
  // This must run AFTER auto-scroll check so we can detect changes
  useLayoutEffect(() => {
    isPrepending.current = false;
    prevChildrenCountRef.current = itemCount;
    prevFirstKeyRef.current = firstKey;
    prevLastKeyRef.current = lastKey;
  }, [children, itemCount, firstKey, lastKey]);

  // Check if content fits in viewport (no scrolling) and trigger callbacks
  useLayoutEffect(() => {
    const virtualizer = virtualizerRef.current;
    if (!virtualizer || itemCount === 0) return;

    // Use requestAnimationFrame to ensure virtualizer has measured content
    requestAnimationFrame(() => {
      const { scrollSize, viewportSize } = virtualizer;

      // If content fits within viewport (no scrolling possible)
      if (scrollSize <= viewportSize) {
        // Trigger both callbacks since user is at both top and bottom
        if (!topReachedRef.current && onTopReached) {
          topReachedRef.current = true;
          onTopReached();
        }
        if (!bottomReachedRef.current && onBottomReached) {
          bottomReachedRef.current = true;
          onBottomReached();
        }
      }
    });
  }, [children, itemCount, onTopReached, onBottomReached]);

  const handleTopReached = useCallback(() => {
    if (!topReachedRef.current && onTopReached) {
      topReachedRef.current = true;
      bottomReachedRef.current = false;
      onTopReached();
    }
  }, [onTopReached]);

  const handleBottomReached = useCallback(() => {
    if (!bottomReachedRef.current && onBottomReached) {
      bottomReachedRef.current = true;
      topReachedRef.current = false;
      onBottomReached();
    }
  }, [onBottomReached]);

  const handleScroll = useCallback(
    (offset: number) => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;

      const { scrollSize, viewportSize } = virtualizer;

      // Track if user is at bottom (for auto-scroll behavior on new messages)
      // Uses -1.5 threshold to account for sub-pixel values when devicePixelRatio has decimal
      shouldStickToBottom.current = offset - scrollSize + viewportSize >= -1.5;

      // Detect scrolled to top (load older messages)
      if (offset <= threshold) {
        handleTopReached();
      } else {
        // Reset flag when scrolled away from top
        topReachedRef.current = false;
      }

      // Detect scrolled to bottom
      if (offset - scrollSize + viewportSize >= -threshold) {
        handleBottomReached();
      } else {
        // Reset flag when scrolled away from bottom
        bottomReachedRef.current = false;
      }
    },
    [threshold, handleTopReached, handleBottomReached]
  );

  return (
    <div
      className={cn("overflow-auto content-end", className)}
      onFocusCapture={() => {
        onFocusWithin?.({
          isAtBottom: bottomReachedRef.current,
          isAtTop: topReachedRef.current,
        });
      }}
      onClickCapture={() => {
        onClickWithin?.({
          isAtBottom: bottomReachedRef.current,
          isAtTop: topReachedRef.current,
        });
      }}
    >
      <Virtualizer ref={virtualizerRef} shift={isPrepending.current} onScroll={handleScroll}>
        {children}
      </Virtualizer>
    </div>
  );
}
